import { faqEntryHref } from "./href";
import type { FaqCategory, FaqEntry } from "./types";

/**
 * Single source of public product FAQ content.
 * Stable ids are deep-link anchors (`/faq#<id>`).
 * One full answer per fact — repeats are omitted, not pointed at.
 */
export const FAQ_ENTRIES: readonly FaqEntry[] = [
  {
    id: "areas-served",
    question: "What areas do you serve?",
    answer: "Currently, we serve only Lagos. We’ll add more cities as we grow.",
  },
  {
    id: "why-trust-sell-it-off",
    question: "Why should I trust Sell It Off?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "We’re a registered company. Don’t take only our word for it — look us up on the ",
          {
            kind: "link",
            href: "https://search.cac.gov.ng/",
            label: "CAC public search",
          },
          " (Corporate Affairs Commission).",
        ],
      },
      {
        type: "paragraph",
        parts: [
          "You can also search for Sell It Off online and see what comes up. Yes, we know “we’re legit” is exactly what anyone can type. Check the CAC record and what you find about us, then decide.",
        ],
      },
    ],
  },
  {
    id: "protect-buyers-sellers",
    question: "How does Sell It Off protect both buyers and sellers?",
    answer: [
      {
        type: "paragraph",
        parts: ["Every sale follows the same path:"],
      },
      {
        type: "orderedList",
        items: [
          ["The buyer pays Sell It Off online — not cash to the seller."],
          [
            "The buyer gets seller contact details and picks a meetup time in the app within 48 hours.",
          ],
          [
            "At meetup, the buyer inspects the item and confirms the sale if they decide to pick it.",
          ],
          ["After that, we pay the seller immediately."],
        ],
      },
      {
        type: "paragraph",
        parts: [
          "So a buyer doesn't pay for something they haven't seen yet, and a seller doesn't hand over their item without a completed sale. We are committed to protecting both buyers and sellers.",
        ],
      },
    ],
  },
  {
    id: "create-account",
    question: "How do I create an account?",
    answer:
      "Creating an account is easy! On mobile, click on the 3 lines menu button in the top right corner (on desktop, click on the 'Sign Up' button in the top right corner), click on 'Sign Up' and fill in your details. Check your email for verification. Once verified, you can start buying and selling immediately.",
  },
  {
    id: "update-profile",
    question: "How do I update my profile information?",
    answer:
      "To update your profile, log in to your account and click on your profile icon on the menu bar (on desktop, click on the down arrow icon in the top right corner, select 'Your Profile' from the dropdown menu). From there, you can update your personal information, contact details, and location.",
  },
  {
    id: "how-to-list",
    question: "How do I list an item for sale?",
    answer:
      "On mobile, tap Sell in the menu bar. On desktop, click Sell in the top bar. Add the title, description, price, and photos, then submit. Our team reviews the listing before it goes live.",
  },
  {
    id: "list-several-items",
    question: "Can I list several items at once?",
    answer:
      "Yes. On the sell page, choose Several items (2–10) at the top. You enter location and pickup details once, then add each item's photos and details. When everything looks good, review and submit the whole batch together.",
  },
  {
    id: "what-can-i-sell",
    question: "What can I sell on Sell It Off?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "Neatly used household goods that work as described — for example smart tvs, ac units, furniture, kitchenware, small appliances in good condition, home décor, etc. Check our ",
          { kind: "link", href: "/terms", label: "Terms of Service" },
          " for the full accepted and prohibited lists (more categories might be added in the future).",
        ],
      },
    ],
  },
  {
    id: "items-not-accepted",
    question: "What items are not accepted on Sell It Off?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "Not allowed: clothing and fashion accessories, vehicles, food/perishables, broken/faulty electronics, illegal or counterfeit goods, hazardous materials, live animals, and digital-only products. Full list: ",
          { kind: "link", href: "/terms", label: "Terms of Service" },
          ".",
        ],
      },
    ],
  },
  {
    id: "listing-photo-tips",
    question: "How should I take photos for my listing?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "You need exactly 3 photos per listing. To give your item the best chance of approval and a faster sale:",
        ],
      },
      {
        type: "unorderedList",
        items: [
          ["Use good lighting — natural daylight works best."],
          [
            "Take photos of the actual item you're selling, do not remove the background of the image. We do not accept photos gotten from the internet, it must be the real picture of your item(s).",
          ],
          [
            "Show the item from different angles (e.g. front, side, close-up of any wear or detail).",
          ],
          [
            "If the item has any minor flaws, include a clear photo of them — honesty builds buyer trust and prevents refund disputes at pickup.",
          ],
        ],
      },
    ],
  },
  {
    id: "listing-pricing-tips",
    question: "How should I price my item?",
    answer:
      "Aim for about 30–50% of the original retail value, depending on age, condition, and demand. However, we provide you with pricing options that we think will work best. If the price options looks like they are too high, you can always adjust them to a more reasonable price to get more buyers. Listings must be between ₦20,000 and ₦3,000,000.",
  },
  {
    id: "listing-approval-criteria",
    question: "What does Sell It Off look for when approving a listing?",
    answer:
      "Our admin team reviews every listing for four things: (1) the item belongs to an accepted household category, (2) the photos clearly show the actual item and its real condition, and (3) the title, description, and location are accurate and consistent with the photos, (4) the price is reasonable and realistic for the used items market. Listings that meet these standards are usually approved within 24 hours.",
  },
  {
    id: "listing-approval-time",
    question: "How long does it take for my listing to be approved?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "Our admin team typically reviews and approves listings within 24 hours. If your listing hasn't been approved within this timeframe, please check if it meets our quality standards or ",
          {
            kind: "link",
            href: "/contact-us",
            label: "contact our support team",
          },
          " for assistance.",
        ],
      },
    ],
  },
  {
    id: "listing-rejected-what-to-do",
    question: "My listing was rejected — what do I do?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "Review the rejection reason carefully, fix the issue (better photos, clearer description, correct category, more realistic price), and resubmit. If you're unsure why your listing wasn't approved or you believe it was rejected in error, ",
          {
            kind: "link",
            href: "/contact-us",
            label: "contact our support team",
          },
          " and we'll guide you.",
        ],
      },
    ],
  },
  {
    id: "edit-listing-after-approval",
    question: "Can I edit my listing after approval?",
    answer:
      "After approval, the live listing is locked — price and details stay as buyers saw them. That avoids a moving target. If you make an edit after approval, it will have to go through review again. If the item is sold somewhere else, mark it as sold elsewhere from My Stuff.",
  },
  {
    id: "when-sellers-get-paid",
    question: "When do I receive payment for my sold item?",
    answer:
      "After you meet the buyer and enter their pickup code in the app, we immediately pay you the sale price minus our 10% fee. Ensure to add your bank details under Profile → Get paid so we know where to send it. You’re paid after that handover of the item.",
  },
  {
    id: "get-more-buyers",
    question: "How do I get more buyers to see my listing?",
    answer:
      "Sell It Off automatically surfaces approved listings to nearby buyers and to buyers searching for relevant items. The best way to attract more interest is a clear title, honest description, sharp photos, fair price, and accurate location.",
  },
  {
    id: "sio-fees",
    question: "How much does Sell It Off charge?",
    answer:
      "Listing and browsing are free. On a successful sale we take 10% from the seller’s payout. No other listing fees.",
  },
  {
    id: "find-items-near-me",
    question: "How do I find items near me?",
    answer:
      "Our platform automatically shows you items based on your location. You can also use the search bar and filters to find specific items by category, location, or price range. The distance from your location is displayed on each listing to help you make informed decisions.",
  },
  {
    id: "notifications-browser",
    question: "How do I enable browser notifications?",
    answer:
      "On desktop Chrome/Edge/Firefox, allow notifications when prompted or in site settings. On iPhone, add Sell It Off to your Home Screen (PWA), open the app from the icon, then enable push under Profile → Notifications → Browser push devices. If you previously blocked notifications, open your browser or device settings, find Sell It Off, and allow alerts for this site.",
  },
  {
    id: "near-me-alerts",
    question: "How do notification for items close to me work?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "We can notify you when new listings are approved close to the location in your profile. You can select the channels you want to receive notifications from under ",
          {
            kind: "link",
            href: "/profile?tab=notifications",
            label: "Profile → Notifications",
          },
          ": in-app alerts, optional browser push, email, WhatsApp (needs a valid phone on your profile), and Telegram.",
          {
            kind: "link",
            href: faqEntryHref("telegram-listing-channels"),
            label: "Telegram listing channels",
          },
          " are browse-anytime feeds — not personalized to your exact area.",
        ],
      },
    ],
  },
  {
    id: "telegram-listing-channels",
    question: "How do I follow listings on Telegram?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "We post approved listings to public Telegram channels. Tap Join / Follow for your area. You can also get private DMs from our Telegram bot which message you about items near your profile location that you choose to know about (got to Profile → Notifications).",
        ],
      },
      { type: "embed", name: "telegram-listing-channels" },
    ],
  },
  {
    id: "how-payments-work",
    question: "How do payments work?",
    answer:
      "You pay Sell It Off online (card or bank transfer are allowed). We hold that money until you confirm that you have recieved your item. Then we pay the seller.",
  },
  {
    id: "after-payment",
    question: "What happens after I make a payment?",
    answer:
      "Inside the app, you’ll get the seller’s contact details, pick a meetup time in the app within 48 hours, stay in touch, then meet and inspect the item. If you’re satified with the item, give the seller your pickup code to finish the sale. After that, we release the seller's oayment.",
  },
  {
    id: "why-pickup-48-hours",
    question: "Why is pickup required within 48 hours of payment?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "The 48-hour window keeps deals moving and respects the seller’s time — they’re holding the item for you. After you pay, ",
          {
            kind: "bold",
            text: "schedule meetup in the app within 48 hours",
          },
          " and stay reachable. If you don’t, the reserved sale may be released and the item unlocked for other buyers. If a refund is then due, a ",
          { kind: "bold", text: "5% administrative fee" },
          " may be deducted before the rest is returned to you. See our ",
          { kind: "link", href: "/refund-policy", label: "Refund Policy" },
          ".",
        ],
      },
    ],
  },
  {
    id: "arrange-pickup",
    question: "How do I arrange pickup for an item?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "After you pay, you’ll get the seller’s contact details. ",
          {
            kind: "bold",
            text: "Pick a meetup time in the app within 48 hours",
          },
          ", message the seller to confirm, and please (IMPORTANT) meet in a safe public place in daylight preferrably. Make sure to carefully inspect the item before you give the seller your pickup code.",
        ],
      },
    ],
  },
  {
    id: "meetup-safety",
    question: "How do I ensure safety during meetup?",
    answer: [
      {
        type: "unorderedList",
        items: [
          [
            "Meet in a busy public place in daylight, such as a mall, a bank, or a fuel station with people around.",
          ],
          [
            "If the item is large and you have to collect it at a home, go with someone else.",
          ],
          [
            "Do not pay the seller cash or send them money on the side. Your payment stays with Sell It Off until you give the pickup code.",
          ],
          [
            "Inspect the item before you give the seller your pickup code. If something feels wrong, leave and contact support.",
          ],
          [
            "Do not share your home address, bank details, or other personal information just to meet.",
          ],
        ],
      },
      {
        type: "paragraph",
        parts: [
          "Sell It Off does not take responsibility for your personal safety when you meet. You choose the place and you attend at your own risk. We are not liable for injury, loss, or harm that happens at a meetup. See ",
          { kind: "link", href: "/trust-safety", label: "Trust & Safety" },
          " and our ",
          { kind: "link", href: "/terms", label: "Terms of Service" },
          ".",
        ],
      },
    ],
  },
  {
    id: "item-not-as-described",
    question: "What if an item isn't as described?",
    answer:
      "At meetup, if it doesn’t match the listing or has an undisclosed major problem, don’t give the seller your pickup code. Contact support right away for a full refund (Help in the app, Contact Us, WhatsApp, or email). After the sale is finished in the app, we can’t refund for issues you could have spotted at inspection time.",
  },
  {
    id: "is-delivery-available",
    question: "Is delivery available?",
    answer:
      "No — Sell It Off doesn’t deliver presently. You have to meet the seller at the agreed pickup location. If you need help moving the item home, you can arrange that by yourself. We’ll announce it clearly if delivery ever becomes a service we offer.",
  },
  {
    id: "contact-support",
    question: "How can I contact support?",
    answer: [
      {
        type: "paragraph",
        parts: [
          "Use the Help button in the app (FAQs or chat with support), the ",
          { kind: "link", href: "/contact-us", label: "Contact Us" },
          " page, email support@sellitoffnow.com, WhatsApp https://wa.me/2348125554459, or Telegram ",
          {
            kind: "link",
            href: "https://t.me/sellitoffnow",
            label: "t.me/sellitoffnow",
          },
          ". We aim to reply as quickly as we can during business hours.",
        ],
      },
    ],
  },
];

export const FAQ_CATEGORIES: readonly FaqCategory[] = [
  {
    id: "about",
    title: "About Sell It Off",
    entryIds: [
      "areas-served",
      "why-trust-sell-it-off",
      "protect-buyers-sellers",
    ],
  },
  {
    id: "account",
    title: "Account",
    entryIds: ["create-account", "update-profile"],
  },
  {
    id: "selling",
    title: "Selling",
    entryIds: [
      "how-to-list",
      "list-several-items",
      "what-can-i-sell",
      "items-not-accepted",
      "listing-photo-tips",
      "listing-pricing-tips",
      "listing-approval-criteria",
      "listing-approval-time",
      "listing-rejected-what-to-do",
      "edit-listing-after-approval",
      "when-sellers-get-paid",
      "sio-fees",
      "get-more-buyers",
    ],
  },
  {
    id: "buying",
    title: "Buying",
    entryIds: [
      "find-items-near-me",
      "how-payments-work",
      "after-payment",
      "why-pickup-48-hours",
      "arrange-pickup",
      "meetup-safety",
      "item-not-as-described",
      "is-delivery-available",
      "notifications-browser",
      "near-me-alerts",
      "telegram-listing-channels",
    ],
  },
  {
    id: "support",
    title: "Support",
    entryIds: ["contact-support"],
  },
];
